/**
 * Referral attribution on the visitor's device (FE-15 FR-FE15-11, §7): local storage `sos-ref`
 * `{code, first_seen}` plus the first-party cookie `sos_ref` (90 days). First-touch: an unexpired
 * attribution with another code is never replaced. The API sets the same cookie on
 * `GET /v1/public/referrals/{code}` (BE-18 C9); this mirrors it for a cross-origin API and keeps the
 * copy `RegisterForm.resolveReferral` reads (FE-05 FR-FE05-14).
 */

export const REFERRAL_CODE_RE = /^[A-Za-z0-9-]{3,20}$/;
export const ATTRIBUTION_DAYS = 90;
export const ATTRIBUTION_MAX_AGE_S = ATTRIBUTION_DAYS * 24 * 60 * 60;

export const STORAGE_KEY = "sos-ref";
export const COOKIE_NAME = "sos_ref";

export interface Attribution {
  code: string;
  /** ISO timestamp of the first visit with this code. */
  first_seen: string;
}

/** FR-FE15-09: `^[A-Za-z0-9-]{3,20}$`, upper-cased; anything else → `null`. */
export function normalizeReferralCode(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim();
  return REFERRAL_CODE_RE.test(value) ? value.toUpperCase() : null;
}

function readStored(): Attribution | null {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null") as {
      code?: unknown;
      first_seen?: unknown;
    } | null;
    const code = normalizeReferralCode(typeof parsed?.code === "string" ? parsed.code : null);
    if (!code) return null;
    const first_seen = typeof parsed?.first_seen === "string" ? parsed.first_seen : "";
    return { code, first_seen };
  } catch {
    return null;
  }
}

function readCookie(): string | null {
  const prefix = `${COOKIE_NAME}=`;
  const entry = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(prefix));
  return entry ? normalizeReferralCode(decodeURIComponent(entry.slice(prefix.length))) : null;
}

function isExpired(attribution: Attribution, now: Date): boolean {
  const seen = new Date(attribution.first_seen).getTime();
  if (!Number.isFinite(seen)) return true;
  return now.getTime() - seen > ATTRIBUTION_MAX_AGE_S * 1000;
}

function writeStored(attribution: Attribution): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Private mode: the cookie (and the API's copy of it) still carries the code.
  }
}

function writeCookie(code: string): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(code)}; Path=/; SameSite=Lax; Max-Age=${ATTRIBUTION_MAX_AGE_S}${secure}`;
}

/**
 * The attribution in force: the unexpired `sos-ref` entry, or else the `sos_ref` cookie (set by the
 * API, or by a visit whose local storage was cleared). `null` when there is none.
 */
export function readAttribution(now: Date = new Date()): Attribution | null {
  const stored = readStored();
  if (stored && !isExpired(stored, now)) return stored;
  const fromCookie = readCookie();
  return fromCookie ? { code: fromCookie, first_seen: now.toISOString() } : null;
}

/**
 * Store `code` as the visitor's attribution unless an unexpired one already exists (first-touch,
 * §8.2 "the first one wins"). Returns the code that is in force afterwards.
 */
export function storeAttribution(code: string, now: Date = new Date()): string {
  const existing = readAttribution(now);
  if (existing) {
    // A cookie-only attribution gets its local copy so the register form finds it offline too.
    if (!readStored() || isExpired(readStored()!, now)) writeStored(existing);
    return existing.code;
  }
  const attribution: Attribution = { code, first_seen: now.toISOString() };
  writeStored(attribution);
  writeCookie(code);
  return code;
}
